<template>
	<EditorShell>
		<!-- Header -->
		<CLayoutEditorHeader
			:isLoading="isLoading"
			:headline="template ? template.name : null"
			:saveButton="$t('templates.save')"
			@back:click="$router.back()"
			@save:click="updateTemplate"
		>
			<EditorSaveChangesPrompt :isVisible="hasChanged" slot="middle" />
		</CLayoutEditorHeader>

		<!-- Wrapper -->
		<JBox class="s-max-w-5xl s-mx-auto" :class="{ 's-max-w-full s-px-4': showPreview }">
			<!-- Preview switcher -->
			<EditorPreviewSwitcher
				:showPreview="showPreview"
				:label="$t('templates.graphQlVariables')"
				class="s-mt-4 s-h-10"
				@preview:toggle="showPreview = $event"
				@preview:open="renderPreview"
				@render:click="renderPreview"
				v-model="previewVariables"
				@input="invalidateDataForPreview"
			>
				<JButton variant="default-light-sm" class="s-mr-2" slot="post-buttons" @click="toggleSendTestModal">
					{{ $t("templates.sendTestEmail") }}
				</JButton></EditorPreviewSwitcher
			>

			<!-- Editor and Preview -->
			<JFlex class="s-mb-4 s-bg-white s-rounded-md s-border s-border-gray-300 s-p-2">
				<!-- Editor -->
				<CCodeEditor height="100vh" :class="[{ 's-w-full': !showPreview }]" :language="editorLanguage" v-model="editorContent" />

				<!-- Preview -->
				<Transition enter-active-class="animated fadeIn faster">
					<JBox
						v-if="showPreview"
						class="s-w-1/2 s-border s-border-gray-300 s-rounded s-bg-white s-ml-1 s-h-screen s-overflow-scroll"
					>
						<JBox v-if="isPreviewLoading" class="s-mt-3">
							<JSpinner type="dots" :delay="0" />
						</JBox>
						<JBox v-else>
							<div
								v-if="editorMode === 'plaintext'"
								class="s-font-mono s-text-sm s-whitespace-pre-wrap s-p-4 s-bg-gray-50 s-border s-border-gray-200 s-rounded-md"
								v-text="previewResult"
							/>
							<div v-else v-html="previewResult" />
						</JBox>
					</JBox>
				</Transition>
			</JFlex>

			<!-- Document Settings -->
			<EditorAccordion :headline="$t('templates.document_settings')" class="s-mb-4">
				<EditorDocumentSettings v-model="template">
					<CFormInput
						name="subject"
						:label="$t('templates.subject')"
						componentName="JInputText"
						v-model="template.subject"
						class="s-mt-6"
					/>
				</EditorDocumentSettings>
			</EditorAccordion>

			<!-- Template Graphql Queries -->
			<EditorAccordion :headline="$t('core.graphQlQuery')" class="s-mb-4">
				<CGraphQlQueryLibrary v-model="template.graphQlQuery" @input="invalidateDataForPreview" />
			</EditorAccordion>

			<!-- Attachments -->
			<EditorAccordion :headline="$t('templates.attachments')" class="s-mb-4">
				<EmailAttachments
					class="s-border s-border-gray-300 s-rounded s-mt-2"
					:templateId="id"
					:attachments="attachments"
					@attachment:added="fetchTemplate"
					@attachment:removed="fetchTemplate"
				/>
			</EditorAccordion>

			<!-- Delete template-->
			<CDialogProvider
				:headline="$t('templates.delete_template')"
				:description="$t('templates.delete_dialog')"
				:cancelButton="$t('templates.continueEditing')"
				:confirmButton="$t('templates.delete_template')"
				@confirm="deleteTemplate"
			>
				<JFlex class="s-justify-end s-mb-4" slot-scope="{ openDialog }">
					<JButton variant="error" @click="openDialog">{{ $t("templates.delete_template") }} </JButton>
				</JFlex>
			</CDialogProvider>
		</JBox>

		<!-- Debug Console -->
		<EditorDebugConsole :errors="errors" />

		<!-- Send Test Modal -->
		<CModal :isVisible="showSendTestModal">
			<JBox class="s-p-6">
				<CFormInput
					name="name"
					:label="$t('templates.emailAddress')"
					componentName="JInputText"
					v-model="emailAddress"
					class="s-mb-6"
				/>
				<JLabel class="s-mr-2 s-font-semibold s-text-sm s-text-gray-900">{{ $t("templates.graphQlVariables") }}</JLabel>
				<CCodeEditor v-model="previewVariables" class="s-mb-4 s-h-48" />
				<JFlex class="s-justify-between">
					<JButton @click="toggleSendTestModal" class="s-mr-2" variant="white">{{ $t("templates.cancel") }}</JButton>
					<JButton @click="sendTestEmail" variant="default-light">{{ $t("templates.send") }}</JButton>
				</JFlex>
				<JSpinner v-if="isLoading" :delay="20" type="dots" />
			</JBox>
		</CModal>
	</EditorShell>
</template>

<script>
import EditorShell from "../components/EditorShell"
import EmailAttachments from "../components/emailMjml/EmailAttachments"
import EditorAccordion from "../components/EditorAccordion"
import EditorSaveChangesPrompt from "../components/EditorSaveChangesPrompt"
import EditorPreviewSwitcher from "../components/EditorPreviewSwitcher"
import EditorDocumentSettings from "../components/EditorDocumentSettings"
import EditorDebugConsole from "../components/EditorDebugConsole"
import { preventBrowserBackButton } from "../utils/preventBrowserBackButton"
import api from "@/api/api"
import { query, mutation } from "../api/queries"
import isEqual from "lodash.isequal"
import { deepClone } from "@/utils/"
import { addHintToLiquidErrorString } from "../utils/addHintToLiquidErrorString"

export default {
	props: {
		id: {
			type: String,
			required: true,
		},
	},
	components: {
		EditorShell,
		EmailAttachments,
		EditorAccordion,
		EditorDocumentSettings,
		EditorPreviewSwitcher,
		EditorSaveChangesPrompt,
		EditorDebugConsole,
	},
	data() {
		return {
			isLoading: false,
			template: {},
			originalTemplate: {},

			attachments: [],
			emailAddress: null,

			showPreview: false,
			isPreviewLoading: false,
			previewResult: null,
			previewData: null,
			previewVariables: null,
			showSendTestModal: false,

			errors: [],
		}
	},

	computed: {
		hasChanged() {
			return !isEqual(this.template, this.originalTemplate)
		},

		// Auto-detect editor mode based on template data
		// Stay in plaintext mode when template was loaded as plaintext, even if user clears content
		editorMode() {
			const hasPlainTextContent = Boolean(this.template?.plainText?.trim?.())
			const wasLoadedAsPlainText = Boolean(this.originalTemplate?.plainText?.trim?.())
			return hasPlainTextContent || wasLoadedAsPlainText ? "plaintext" : "mjml"
		},

		// Get the appropriate editor language
		editorLanguage() {
			return this.editorMode === "plaintext" ? "plaintext" : "html"
		},

		// Get the current editor content binding
		editorContent: {
			get() {
				return this.editorMode === "plaintext" ? this.template?.plainText || "" : this.template?.mjml || ""
			},
			set(value) {
				if (this.editorMode === "plaintext") {
					this.$set(this.template, "plainText", value)
				} else {
					this.$set(this.template, "mjml", value)
				}
			},
		},
	},

	methods: {
		toggleSendTestModal() {
			this.showSendTestModal = !this.showSendTestModal
		},

		async fetchTemplate() {
			if (this.isLoading) return
			this.setErrors()

			this.isLoading = true
			const res = await api({
				query: query.fetchEmailTemplate,
				variables: { where: { id: this.id } },
			})
			this.isLoading = false

			if (!res?.data?.templates?.list?.[0]) {
				this.setErrors(res?.errors?.map(item => item?.message) || [this.$t("templates.noTemplate")])
				return
			}

			this.template = res?.data?.templates?.list?.[0] ?? {}
			this.originalTemplate = deepClone(res?.data?.templates?.list?.[0] ?? {})
		},

		async updateTemplate() {
			if (this.isLoading) return
			this.setErrors()

			const input = {
				graphQlQuery: this.template?.graphQlQuery,
				name: this.template?.name,
				description: this.template?.description,
				logicalId: this.template?.logicalId,
				subject: this.template?.subject,
			}

			if (this.editorMode === "plaintext") {
				const plainTextContent = this.template?.plainText ?? ""
				if (!plainTextContent || !plainTextContent.trim()) {
					this.setErrors([this.$t("templates.plainTextRequired")])
					return
				}
				input.plainText = plainTextContent
			} else {
				input.mjml = this.template?.mjml
			}

			this.isLoading = true
			const res = await api({
				query: mutation.updateTemplate,
				variables: {
					templateId: this.id,
					input,
				},
			})
			this.isLoading = false

			if (res?.data?.updateEmailMjmlTemplate?.status !== "success") {
				this.setErrors(res?.data?.updateEmailMjmlTemplate?.errors || res?.errors?.map(item => item?.message))
				return
			}

			this.originalTemplate = deepClone(this.template)
		},

		async sendTestEmail() {
			if (this.isLoading) return
			this.setErrors()

			this.isLoading = true
			const res = await api({
				query: mutation.sendNotification,
				variables: {
					input: {
						type: "testMjmlTemplate",
						emailMessage: {
							to: this.emailAddress,
							from: "test@covergo.com",
							subject: this?.template?.subject || "No subject",
							templateRendering: {
								templateId: this.id,
								input: {
									name: "data",
									variablesJsonString: this.previewVariables ? this.previewVariables : "{}",
								},
							},
						},
					},
				},
			})
			this.isLoading = false

			if (res?.data?.sendNotification?.status !== "success") {
				this.setErrors(res?.data?.sendNotification?.errors || res?.errors?.map(item => item?.message))
				return
			}

			this.showSendTestModal = false
		},

		async deleteTemplate() {
			if (this.isLoading) return
			this.setErrors()

			this.isLoading = true
			const res = await api({
				query: mutation.deleteTemplate,
				variables: {
					templateId: this.id,
				},
			})
			this.isLoading = false

			if (res?.data?.deleteTemplate?.status !== "success") {
				this.setErrors(res?.data?.deleteTemplate?.errors || res?.errors?.map(item => item?.message))
				return
			}

			this.$router.push({ name: "ViewTemplatesHome" })
		},

		invalidateDataForPreview() {
			this.previewData = null
		},

		async getMemoizedDataForPreview() {
			if (this.previewData) {
				return Promise.resolve({ isSuccess: true, errors: null, data: this.previewData })
			}

			const query = this?.template?.graphQlQuery ?? ""
			let variables = { limit: 1 }

			if (!query) return Promise.resolve({ isSuccess: true, errors: null, data: null })

			try {
				variables = { ...variables, ...JSON.parse(this.previewVariables) }
			} catch (e) {
				return Promise.resolve({ isSuccess: false, errors: [e], data: null })
			}

			const res = await api({ query, variables })

			if (res?.errors) {
				return Promise.resolve({ isSuccess: false, errors: res?.errors?.map(item => item.message), data: null })
			}

			this.previewData = res?.data
			return Promise.resolve({ isSuccess: true, data: res?.data, errors: null })
		},

		async renderPreview() {
			if (this.isLoading) return
			this.isPreviewLoading = true
			this.previewResult = ""
			this.setErrors()

			const { isSuccess, errors, data } = await this.getMemoizedDataForPreview()
			if (!isSuccess) {
				this.setErrors(errors, { hint: this.$t("templates.problemWithGql") })
				this.isPreviewLoading = false
				return
			}

			const res = await api({
				query: this.editorMode === "plaintext" ? query.renderEmailPlainText : query.renderEmailMjml,
				variables:
					this.editorMode === "plaintext"
						? {
								emailPlainText: this?.template?.plainText || "",
								input: {
									name: "data",
									contentJsonString: data ? JSON.stringify(data) : "{}",
								},
						  }
						: {
								emailMjml: this?.template?.mjml || "",
								input: {
									name: "data",
									contentJsonString: data ? JSON.stringify(data) : "{}",
								}
						  },
			})
			this.isPreviewLoading = false

			const renderResult = this.editorMode === "plaintext" ? res?.data?.renderEmailPlainText : res?.data?.renderEmailMjml
			if (renderResult?.status !== "success") {
				this.setLiquidErrors(renderResult?.errors || res?.errors?.map(item => item?.message))
				return
			}

			this.previewResult = renderResult?.value
		},

		setLiquidErrors(errors = []) {
			const templateSource = this.editorMode === "plaintext" ? this?.template?.plainText : this?.template?.mjml
			const output = errors.map(
				item => addHintToLiquidErrorString({ message: item, templateSource }) // returns {message, hint} object
			)
			this.errors = output
		},

		setErrors(errors = [], { hint = "" } = {}) {
			this.errors = errors?.map(err => ({ message: err, hint }))
		},
	},

	watch: {
		template: {
			handler() {
				const attachmentReferences = this.template?.attachmentReferences?.map(item => ({ ...item, type: "file" }))
				const attachmentTemplates = this.template?.attachmentTemplates?.map(item => ({ ...item, type: "template" }))
				this.attachments = [...attachmentReferences, ...attachmentTemplates]
			},
			deep: true,
		},
	},

	created() {
		this.fetchTemplate()
	},

	beforeRouteLeave(to, from, next) {
		preventBrowserBackButton({ next, showDialog: this.hasChanged })
	},
}
</script>
